import React from "react";
import { Loader2 } from "lucide-react";
import { useActiveSubjects, transformSubjectsForComponent } from "src/hooks/useSubjects";

export interface Subject {
  id: string;
  name: string;
  icon: React.ReactNode;
  category?: string;
}

interface SubjectSelectionProps {
  selectedSubjects: string[];
  onSubjectsChange: (subjects: string[]) => void;
  showHeader?: boolean;
  headerTitle?: string;
  headerDescription?: string;
  showSelectedCount?: boolean;
  className?: string;
  maxWidth?: string;
  useSimpleToggle?: boolean;
}

const SubjectSelection = React.forwardRef<HTMLDivElement, SubjectSelectionProps>(({
  selectedSubjects,
  onSubjectsChange,
  showHeader = false,
  headerTitle = "What subjects do you teach?",
  headerDescription = "Select all subjects that you're qualified to teach.",
  showSelectedCount = false,
  className = "",
  maxWidth = "max-w-5xl",
  useSimpleToggle = false,
}, ref) => {
  const { data: subjectsData, isLoading, error } = useActiveSubjects();

  // Transform subjects data for component use
  const subjects = subjectsData ? transformSubjectsForComponent(subjectsData) : [];

  // Group subjects by category
  const groupedSubjects = subjects.reduce<Record<string, any[]>>((acc, subj) => {
    const category = subj.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(subj);
    return acc;
  }, {});

  const toggleSubject = (subjectId: string) => {
    const subject = subjects.find(s => s.id === subjectId);
    const subjectName = subject?.name || subjectId;
    
    if (useSimpleToggle) {
      // Simple toggle: use subjectId
      if (selectedSubjects.includes(subjectId)) {
        onSubjectsChange(selectedSubjects.filter((id) => id !== subjectId));
      } else {
        onSubjectsChange([...selectedSubjects, subjectId]);
      }
    } else {
      // Default behavior: use subjectName
    if (selectedSubjects.includes(subjectName)) {
      onSubjectsChange(selectedSubjects.filter((name) => name !== subjectName));
    } else {
      onSubjectsChange([...selectedSubjects, subjectName]);
      }
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div ref={ref} className={`space-y-6 ${maxWidth} mx-auto p-4 ${className}`}>
        {showHeader && (
          <div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 450, fontSize: '1.8rem' }}>{headerTitle}</h2>
            <p className="text-gray-600" style={{ fontFamily: 'Segoe UI, sans-serif' }}>{headerDescription}</p>
          </div>
        )}
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          <span className="ml-2 text-gray-500" style={{ fontFamily: 'Segoe UI, sans-serif' }}>Loading subjects...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div ref={ref} className={`space-y-6 ${maxWidth} mx-auto p-4 ${className}`}>
        {showHeader && (
          <div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 450, fontSize: '1.8rem' }}>{headerTitle}</h2>
            <p className="text-gray-600" style={{ fontFamily: 'Segoe UI, sans-serif' }}>{headerDescription}</p>
          </div>
        )}
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-red-600 mb-2" style={{ fontFamily: 'Segoe UI, sans-serif' }}>Failed to load subjects</p>
            <p className="text-gray-500 text-sm" style={{ fontFamily: 'Segoe UI, sans-serif' }}>Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={`space-y-6 ${maxWidth} mx-auto p-4 ${className}`}>
      {showHeader && (
        <div>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 450, fontSize: '1.8rem' }}>{headerTitle}</h2>
          <p className="text-gray-600" style={{ fontFamily: 'Segoe UI, sans-serif' }}>{headerDescription}</p>
        </div>
      )}

      {Object.entries(groupedSubjects).map(([category, list]) => (
        <div key={category}>
          <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400 }}>{category}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {list.map((subject) => (
              <div
                key={subject.id}
                onClick={() => toggleSubject(subject.id)}
                className={`cursor-pointer p-4 rounded-xl border text-center transition
                  ${selectedSubjects.includes(useSimpleToggle ? subject.id : subject.name)
                    ? 'border-blue-500 bg-blue-50 shadow'
                    : 'border-gray-200 hover:border-gray-400'}`}
              >
                <div className="text-3xl mb-2">{subject.icon}</div>
                <div className="font-medium" style={{ fontFamily: 'Segoe UI, sans-serif', fontWeight: 350 }}>{subject.name}</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {showSelectedCount && (
        <div className="mt-6 text-sm text-gray-700" style={{ fontFamily: 'Segoe UI, sans-serif' }}>
          ✅ Selected: {selectedSubjects.length} subject{selectedSubjects.length !== 1 && 's'}
        </div>
      )}
    </div>
  );
});

SubjectSelection.displayName = "SubjectSelection";

export default SubjectSelection;